import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CTAButtons from "@/components/CTAButtons";
import SectionHeader from "@/components/SectionHeader";
import TrustBar from "@/components/TrustBar";
import { services } from "@/data/services";

export default function Services() {
    const navigate = useNavigate();
    return (
        <div data-testid="page-services">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-28">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">Services · Established 2015 · CA(SA) · RA</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            Fifteen disciplines.
                            <span className="block text-tca-orange">One trusted firm.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            Whether you need a statutory audit, a SARS dispute resolved or a clean set of monthly Xero books — TC Auditors covers the full compliance and advisory cycle for South African businesses.
                        </p>
                        <CTAButtons className="mt-9" testIdPrefix="services-hero-cta" />
                    </div>
                </div>
            </section>

            {/* Services grid */}
            <section className="bg-white">
                <div className="container-tca py-20 md:py-28">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-tca-line border border-tca-line">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            const isAlt = i % 2 !== 0;
                            return (
                                <article
                                    key={s.slug}
                                    data-testid={`service-${s.slug}`}
                                    onClick={s.slug === "personal-tax" ? () => navigate("/pricing", { state: { openView: "personal" } }) : s.slug === "accounting" ? () => navigate("/pricing", { state: { openView: "company" } }) : undefined}
                                    className={`p-8 lg:p-10 lift group transition-colors duration-200 ${
                                        isAlt ? "bg-orange-100" : "bg-white"
                                    } hover:bg-tca-orange${s.slug === "personal-tax" || s.slug === "accounting" ? " cursor-pointer" : ""}`}
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="w-12 h-12 bg-tca-orangeSoft group-hover:bg-white/20 text-tca-orange group-hover:text-white flex items-center justify-center transition-colors duration-200">
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <span className="text-tca-line group-hover:text-white/30 text-sm tabular-nums transition-colors duration-200">{(i + 1).toString().padStart(2, "0")}</span>
                                    </div>
                                    <h3 className="font-display font-bold text-2xl text-tca-ink group-hover:text-white mb-4 transition-colors duration-200">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-tca-mute group-hover:text-white/80 leading-relaxed mb-5 transition-colors duration-200">{s.summary}</p>
                                    <ul className="space-y-2">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2 text-sm text-tca-ink group-hover:text-white/90 transition-colors duration-200">
                                                <Check className="w-4 h-4 mt-0.5 text-tca-orange group-hover:text-white shrink-0 transition-colors duration-200" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* Process */}
            <section className="bg-tca-fog border-y border-tca-line">
                <div className="container-tca py-20 md:py-28">
                    <SectionHeader
                        overline="How we work"
                        title="A process that respects your time."
                        description="We build a written engagement letter, set up Xero, run the monthly cycle, and prepare your year-end set — every step has a name and an owner at TCA."
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-tca-line border border-tca-line">
                        {[
                            { step: "01", title: "Diagnostic call", text: "30-min Zoom or in-office. We map your current setup and flag risks." },
                            { step: "02", title: "Engagement letter", text: "Scope, fees, timelines and POPI / IRBA disclosures, signed both sides." },
                            { step: "03", title: "Onboarding", text: "Xero set-up, bank feeds, master files, prior-year migration if needed." },
                            { step: "04", title: "Run the cycle", text: "Monthly books, VAT/PAYE returns, year-end AFS and SARS submissions." },
                        ].map((s, i) => (
                            <div key={s.step} data-testid={`process-${s.step}`}
                                className={`p-8 lg:p-10 lift group transition-colors duration-200 ${i % 2 !== 0 ? "bg-orange-100" : "bg-white"} hover:bg-tca-orange`}>
                                <p className="font-display font-extrabold text-tca-orange group-hover:text-white text-3xl mb-3 transition-colors duration-200">{s.step}</p>
                                <h3 className="font-display font-bold text-xl text-tca-ink group-hover:text-white mb-2 transition-colors duration-200">{s.title}</h3>
                                <p className="text-sm text-tca-mute group-hover:text-white/80 leading-relaxed transition-colors duration-200">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white">
                <div className="container-tca py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">Not sure which service you need?</h2>
                        <p className="mt-4 text-tca-mute">Send a general inquiry and we will help you triage — or book a virtual meeting straight with our director.</p>
                    </div>
                    <CTAButtons testIdPrefix="services-cta" />
                </div>
            </section>
        </div>
    );
}
